import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {classMap} from 'lit/directives/class-map.js';
import {map} from 'lit/directives/map.js';
import {range} from 'lit/directives/range.js';
import {msg} from '@lit/localize';

import store from '../../store';
import {setHeadTableVisible, setOlivioHeadAngle, setOlivioHeadHeight, setSelectedHead} from '../olivioSlice';
import {
	generateOlivioHeadAngleSelector,
	generateOlivioHeadHeightSelector,
	selectHeadTableVisible,
	selectOlivioDesignIsFloracion,
	selectOlivioDesignIsSistema,
	selectOlivioNumberOfHeads, selectOlivioOffsetFromBottom, selectOlivioOffsetFromTop, selectOlivioPoleHeight,
	selectOlivioSelectedHead
} from '../olivioSelectors';

@customElement('olivio-head-table')
export class OlivioHeadTable extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				width: var(--assistant-widget-width);
			}

			.title {
				background-color: var(--color-widget-background);
				padding: 12px;

				display: flex;
				flex-direction: row;
				align-items: center;
				font: var(--font-widget-label);
				color: var(--color-widget-text);
				justify-content: space-between;
				transition: background-color 0.4s ease-out, color 0.4s ease-out;

			}

			button {
				font: var(--font-widget-label);
				width: 20px;
				height: 20px;
				border-radius: 10px;
				background-color: transparent;
				color: var(--color-black);
				border: 1px solid var(--color-black);;
				cursor: pointer;
				transition: background-color 0.4s ease-out, color 0.4s ease-out;
			}

			.active button {
				background-color: var(--color-black);
				color: var(--color-white);
			}

			button.toggle {
				height: 24px;
				width: 50px;
				border-radius: 12px;
				border: 0 none;
				background-color: var(--color-grey6);
				color: var(--color-widget-border);
			}

			.knob {
				display: block;
				height: 18px;
				width: 18px;
				border-radius: 9px;
				background-color: var(--color-widget-border);
				margin: 0 -10%;
				transition: margin 0.4s ease-out;
			}


			.expanded .title {
				background-color: var(--color-white);
				color: var(--color-black);
			}

			.expanded .toggle {
				background-color: var(--color-grey3);
			}

			.expanded .knob {
				margin: 0 60%;
				color: var(--color-black);
			}


			.table-container {
				max-height: 0;
				background-color: var(--color-white);
				padding: 0;
				opacity: 0;
				overflow: hidden;
				transition: max-height 0.4s ease-out, opacity 0.4s ease-out, padding 0.4s ease-out;
			}

			.expanded .table-container {
				opacity: 1;
				max-height: 400px;
				padding: var(--very-small-padding);
				display: block;
			}

			table {
				border-collapse: collapse;
				background-color: var(--color-white);
				width: 100%
			}

			th {
				font: var(--font-widget-label);
				color: var(--color-grey3);
				padding: var(--very-small-padding);
				text-align: left;
			}

			.cell {
				font: var(--font-widget-label);
				color: var(--color-grey3);
				padding: 6px 12px;
				border: 1px solid var(--color-grey2);
			}

			.cell.position {
				border-left: none;
			}

			.cell.angle {
				border-right: none;
			}

			.cell.label {
				border: none;
				width: 20px;
			}

			input {
				font: var(--font-widget-label);
				color: var(--color-black);
				background-color: transparent;
				padding: 0.5em;
				line-height: 120%;
				margin: 0;
				width: 75%;
				box-sizing: border-box;
				text-align: left;
				border: none;
			}

			.unit {
				display: block;
				float: right;
				text-align: right;
				padding: 6px 0;
			}

        `];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	connectedCallback() {
		super.connectedCallback();
	}
	stateChanged(state) {
		this.state = state;
		this.numberOfHeads = selectOlivioNumberOfHeads(state);
		this.selectedHead = selectOlivioSelectedHead(state);
		this.expanded = selectHeadTableVisible(state);

		this.maxHeadHeight = selectOlivioPoleHeight(state) - selectOlivioOffsetFromTop(state);
		this.minHeadHeight = selectOlivioOffsetFromBottom(state);
	}

	toggle() {
		store.dispatch(setHeadTableVisible({headTableVisible: !this.expanded}));
	}
	setSelected(i) {
		store.dispatch(setSelectedHead({selectedHead: i }));
	}
	setHeight(value, instance) {
		const heightRestrictedValue = Math.max(Math.min(parseFloat(value), this.maxHeadHeight * 100), this.minHeadHeight * 100);
		store.dispatch(setOlivioHeadHeight({instance: instance, value: heightRestrictedValue/100}));
	}
	setAngle(value, instance) {
		const restrictedAngle = Math.max(Math.min(parseFloat(value), 360), 0);
		store.dispatch(setOlivioHeadAngle({instance: instance, value: restrictedAngle}));
	}
	getHeadSettings(instance) {
		const selectHeight = generateOlivioHeadHeightSelector(instance);
		const selectAngle = generateOlivioHeadAngleSelector(instance);

		return html`
			<tr class="row ${classMap({active: this.selectedHead === instance})}">
				<td class="cell position">
					<input type="number" min="${this.minHeadHeight*100}" max="${this.maxHeadHeight*100}" step="1"
						   .value=${Math.round(selectHeight(this.state) * 100)}
						   @change="${e => this.setHeight(e.target.value, instance)}">
					<span class="unit">cm</span>
				</td>
				<td class="cell angle">
					<input type="number" min="0" max="360" step="1"
						   .value=${Math.round(selectAngle(this.state))}
						   @change="${e => this.setAngle(e.target.value, instance)}">
					<span class="unit">°</span>
				</td>
				<td class="cell label">
					<button @click="${(e) => this.setSelected(instance)}">${instance + 1}</button>
				</td>
			</tr>
		`;
	}
	render() {
		const s = store.getState();
		if (!selectOlivioDesignIsSistema(s) && !selectOlivioDesignIsFloracion(s)) return null;

		return html`
			<div class="${classMap({expanded: this.expanded})}">
				<div class="title">
					<span class="headline">${msg('Table view')}</span>
					<span class="switch"><button class="toggle" @click=${this.toggle}><div class="knob"></div></button></span>
				</div>
				<div class="table-container">
					<table class="table">
						<tr class="header">
							<th>${msg('Vertical Pos.')}</th>
							<th>${msg('Angle')}</th>
							<th></th>
						</tr>
						${map(range(this.numberOfHeads), (i) => this.getHeadSettings(i))}
					</table>
				</div>
			</div>

		`;
	}
}
